/**
 * RESPONSIBILITY: dropping cached content. Two operations, kept apart on purpose.
 *
 *   flushLocal()    THIS browser. Always allowed — it is your browser. No network
 *                   call, so it cannot fail and cannot be refused.
 *   flushServer()   EVERYONE. Bumps the shared cache epoch, so every other client
 *                   drops its cache on the next revalidation. Admin only, 403.
 *
 * They used to be one function with a `server: boolean` flag, which meant the local
 * flush was one wrong argument away from broadcasting to every visitor. A blast
 * radius of "me" and a blast radius of "the entire internet" should not share an
 * entry point.
 */
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { FRAMEWORK_ROUTES, HTTP } from '@framework/core';
import { SECTION_KEY_ROOT, HISTORY_KEY_ROOT, PERSISTED_CACHE_KEY, EPOCH_KEY } from './cache-keys';
import { clearCapabilities } from './capability-store';
import type { ServerFlush } from './types';

/**
 * Everything this browser is holding: in-memory queries, the derived permission
 * store, and the copy on disk.
 *
 * The localStorage entry is removed explicitly rather than left to the persister to
 * rewrite. The persister only writes on its own schedule, so a reload immediately
 * after a flush could otherwise rehydrate exactly what was just cleared.
 */
export async function flushLocal(qc: QueryClient): Promise<void> {
  qc.removeQueries({ queryKey: [SECTION_KEY_ROOT] });
  qc.removeQueries({ queryKey: [HISTORY_KEY_ROOT] });
  clearCapabilities();                                  // permissions go with the content

  try {
    window.localStorage.removeItem(PERSISTED_CACHE_KEY);
    window.sessionStorage.removeItem(PERSISTED_CACHE_KEY);
  } catch {
    // Private mode, or storage disabled. The in-memory clear above already happened,
    // and failing here would turn a housekeeping action into an error page.
  }

  await qc.invalidateQueries({ queryKey: [SECTION_KEY_ROOT] });
}

/**
 * The shared epoch. Affects every visitor, so the server enforces admin and answers
 * 403 otherwise — this never assumes, it reports what the server said.
 */
export async function flushServer(): Promise<ServerFlush> {
  try {
    const res = await fetch(FRAMEWORK_ROUTES.flushAll, { method: 'POST', credentials: 'include' });
    return res.ok ? 'done' : res.status === HTTP.forbidden ? 'denied' : 'failed';
  } catch {
    return 'failed';
  }
}

/** Hook forms, so components don't have to thread the QueryClient around. */
export function useFlushLocal(): () => Promise<void> {
  const qc = useQueryClient();
  return () => flushLocal(qc);
}

export function useFlushServer(): () => Promise<ServerFlush> {
  return flushServer;
}

/* -------------------------------------------------------------------------- */

/**
 * The server returns a cache epoch with every query. If it moves, either somebody flushed
 * or the server has restarted — so this client drops what it holds.
 *
 * REMEMBERED ACROSS RELOADS. Held only in memory, a freshly loaded page had no epoch to
 * compare against and accepted whatever arrived, so a deploy could never reach a browser
 * that already had a cache. That is not a stale-content problem but a stale-SHAPE one:
 * the stored row has not changed, the server correctly answers CURRENT and sends no
 * payload, and a field the deploy added is simply absent from the page for as long as
 * that copy lives.
 *
 * LOCAL only, always: we are reacting to a flush, not performing one.
 */
let knownEpoch = 0;

const remembered = (): number => {
  if (knownEpoch) return knownEpoch;
  try {
    knownEpoch = Number(window.localStorage.getItem(EPOCH_KEY)) || 0;
  } catch {
    knownEpoch = 0;                                     // private mode: memory only
  }
  return knownEpoch;
};

const remember = (epoch: number): void => {
  knownEpoch = epoch;
  try { window.localStorage.setItem(EPOCH_KEY, String(epoch)); } catch { /* see above */ }
};

/** Something was cached before this browser started recording epochs. */
const holdsCache = (): boolean => {
  try { return window.localStorage.getItem(PERSISTED_CACHE_KEY) !== null; }
  catch { return false; }
};

export function noteEpoch(epoch: number | undefined, qc: QueryClient): void {
  if (epoch === undefined) return;
  const known = remembered();

  // NEVER SEEN ONE, BUT HOLDING A CACHE. That is a browser whose copy predates this
  // mechanism, and it is precisely the copy most likely to be the wrong shape — so the
  // first sighting flushes rather than quietly accepting what is already there. For a
  // browser with nothing cached this costs nothing, which is why it is not conditional
  // on anything cleverer.
  if (known === 0) {
    remember(epoch);
    if (holdsCache()) void flushLocal(qc);
    return;
  }

  if (epoch !== known) {
    remember(epoch);
    void flushLocal(qc);
  }
}
